import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import {
  Plus,
  Edit,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import inquirySubCategoryMasterService from '../../api/inquirySubCategoryMasterService';
import inquiryCategoryMasterService from '../../api/inquiryCategoryMasterService';
import { getRoleCodeFromId } from '../../utils/roleMapper';

const PAGE_SIZE = 10;
const ALLOWED_ROLES = new Set(['ADMIN', 'SUPER_ADMIN']);

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const emptyForm = () => ({
  inquiryCategoryId: '',
  inquirySubCategoryCode: '',
  inquirySubCategoryName: '',
  description: '',
  status: true
});

/**
 * Inquiry Sub Category Master — ADMIN / SUPER_ADMIN only.
 */
const InquirySubCategoryManagement = () => {
  const roleId = Number(sessionStorage.getItem('roleId') || sessionStorage.getItem('role_id') || 0);
  const roleCode = (getRoleCodeFromId(roleId) || sessionStorage.getItem('roleCode') || '').toUpperCase();
  const canAccess = ALLOWED_ROLES.has(roleCode);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter]);

  const loadCategories = useCallback(async () => {
    if (!canAccess) return;
    try {
      const data = await inquiryCategoryMasterService.getAll(true);
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading inquiry categories:', error);
      setCategories([]);
    }
  }, [canAccess]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const loadPage = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const statusParam =
        statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
      const pageData = await inquirySubCategoryMasterService.search({
        search: debouncedSearch,
        status: statusParam,
        page: currentPage,
        size: PAGE_SIZE
      });
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      setRows(content);
      setTotalPages(pageData?.totalPages ?? 0);
      setTotalElements(pageData?.totalElements ?? content.length);
    } catch (error) {
      console.error('Error loading inquiry sub categories:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load inquiry sub categories');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [canAccess, debouncedSearch, statusFilter, currentPage]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setShowModal(true);
    loadCategories();
  };

  const openEdit = async (row) => {
    setEditing(row);
    setFormData({
      inquiryCategoryId: row.inquiryCategoryId != null ? String(row.inquiryCategoryId) : '',
      inquirySubCategoryCode: row.inquirySubCategoryCode || '',
      inquirySubCategoryName: row.inquirySubCategoryName || '',
      description: row.description || '',
      status: row.status !== false
    });
    setShowModal(true);
    await loadCategories();
    if (row.inquiryCategoryId != null) {
      setCategories((prev) => {
        if (prev.some((c) => Number(c.id) === Number(row.inquiryCategoryId))) return prev;
        return [
          ...prev,
          {
            id: row.inquiryCategoryId,
            inquiryCategoryName: row.inquiryCategoryName || `Category #${row.inquiryCategoryId}`
          }
        ];
      });
    }
  };

  const handleSave = async () => {
    if (!formData.inquiryCategoryId) {
      toast.error('Inquiry category is required');
      return;
    }
    if (!formData.inquirySubCategoryCode?.trim()) {
      toast.error('Inquiry sub category code is required');
      return;
    }
    if (!formData.inquirySubCategoryName?.trim()) {
      toast.error('Inquiry sub category name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        inquiryCategoryId: Number(formData.inquiryCategoryId),
        inquirySubCategoryCode: formData.inquirySubCategoryCode.trim(),
        inquirySubCategoryName: formData.inquirySubCategoryName.trim(),
        description: formData.description?.trim() || '',
        status: formData.status !== false
      };

      if (editing?.id) {
        await inquirySubCategoryMasterService.update(editing.id, payload);
        toast.success('Inquiry sub category updated successfully');
      } else {
        await inquirySubCategoryMasterService.create(payload);
        toast.success('Inquiry sub category created successfully');
      }
      setShowModal(false);
      setEditing(null);
      await loadPage();
    } catch (error) {
      console.error('Error saving inquiry sub category:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save inquiry sub category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const next = !(row.status !== false);
    try {
      await inquirySubCategoryMasterService.updateStatus(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      toast.success(`Inquiry sub category ${next ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling inquiry sub category status:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ListChecks className="h-7 w-7 text-primary" />
            Inquiry Sub Category Master
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage inquiry sub categories under an inquiry category.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPage} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Inquiry Sub Category
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by code, name, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <label className="text-sm font-medium text-foreground mb-1 block">Status</label>
            <StatusFilterDropdown
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Filter by Status"
              onChange={setStatusFilter}
              icon={Filter}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left p-3 text-sm font-semibold">S.No</th>
                  <th className="text-left p-3 text-sm font-semibold">Category</th>
                  <th className="text-left p-3 text-sm font-semibold">Code</th>
                  <th className="text-left p-3 text-sm font-semibold">Name</th>
                  <th className="text-left p-3 text-sm font-semibold">Description</th>
                  <th className="text-left p-3 text-sm font-semibold">Status</th>
                  <th className="text-left p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      No inquiry sub categories found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 text-sm">{currentPage * PAGE_SIZE + index + 1}</td>
                      <td className="p-3 text-sm">{row.inquiryCategoryName || '—'}</td>
                      <td className="p-3 text-sm font-mono">{row.inquirySubCategoryCode}</td>
                      <td className="p-3 text-sm font-medium">{row.inquirySubCategoryName}</td>
                      <td className="p-3 text-sm text-muted-foreground max-w-xs truncate" title={row.description || ''}>
                        {row.description || '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.status !== false}
                            onCheckedChange={() => handleToggleStatus(row)}
                          />
                          <Badge className={row.status !== false
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'}>
                            {row.status !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {totalElements} total · Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 0 || loading}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages - 1 || totalPages === 0 || loading}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editing ? 'Edit Inquiry Sub Category' : 'Add Inquiry Sub Category'}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Inquiry Category *</label>
            <Select
              value={formData.inquiryCategoryId}
              onValueChange={(value) => setFormData((f) => ({ ...f, inquiryCategoryId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select inquiry category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    {cat.inquiryCategoryName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Code *</label>
            <Input
              value={formData.inquirySubCategoryCode}
              onChange={(e) => setFormData((f) => ({ ...f, inquirySubCategoryCode: e.target.value.toUpperCase() }))}
              placeholder="e.g. BILL-SUB"
              maxLength={50}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Name *</label>
            <Input
              value={formData.inquirySubCategoryName}
              onChange={(e) => setFormData((f) => ({ ...f, inquirySubCategoryName: e.target.value }))}
              placeholder="Inquiry sub category name"
              maxLength={150}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.status !== false}
              onCheckedChange={(checked) => setFormData((f) => ({ ...f, status: checked }))}
            />
            <span className="text-sm">Active</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InquirySubCategoryManagement;
